import {
  ArrowUpTrayIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import {
  useBulkProductTagsQuery,
  useListAssetsQuery,
  useTagAssetProductMutation,
  useUntagAssetProductMutation,
} from "~/ui-rtk/api/assetsApi"
import {
  useGetProductFeedMediaQuery,
  useReorderProductFeedMediaMutation,
} from "~/ui-rtk/api/supplementalFeedApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import DebouncedInput from "~/ui-rtk/components/ui/controls/DebouncedInput/DebouncedInput"
import useAssetUpload from "~/ui-rtk/hooks/useAssetUpload"

/**
 * Slot editor for what the supplemental feed serves for this product.
 *
 * The grid always shows every slot the CSV has (hero + image[1..N] +
 * video[0..M]). A filled slot is a MANUAL AssetProductTag (the same thing
 * AdForge's save-to-feed writes, so those land here pre-filled); clicking an
 * empty slot opens a picker over the product snapshot + the asset library.
 * Selections fill slots front-to-back. Once anything is selected the feed
 * serves ONLY the selection; with no selection it auto-pads from the
 * snapshot's categorized image sets.
 */

const LIBRARY_PAGE_SIZE = 24

interface PickerCandidate {
  key: string
  url: string
  /** Group / badge: the snapshot set's taxonomy tag, or "library" / "video". */
  label: string
  /** Human handle shown on hover: the upload's file name, else the URL's last segment. */
  name?: string
  /** Where the asset came from (Uploads, AI, Shopify, …), library assets only. */
  source?: string
  isVideo?: boolean
  assetId?: string
}

/** Picker scopes: this product's own pool, the whole library, or one source. */
type PickerScope =
  | "product"
  | "all"
  | "UPLOAD"
  | "GEN_AI"
  | "SHOPIFY_PRODUCT"
  | "FACEBOOK_AD"
  | "INSTAGRAM"

const SCOPES: Array<{ value: PickerScope; label: string }> = [
  { value: "product", label: "This product" },
  { value: "all", label: "All" },
  { value: "UPLOAD", label: "Uploads" },
  { value: "GEN_AI", label: "AI generated" },
  { value: "SHOPIFY_PRODUCT", label: "Shopify" },
  { value: "FACEBOOK_AD", label: "Ads" },
  { value: "INSTAGRAM", label: "Instagram" },
]

const SOURCE_LABEL: Record<string, string> = {
  UPLOAD: "upload",
  GEN_AI: "AI",
  SHOPIFY_PRODUCT: "Shopify",
  FACEBOOK_AD: "ad",
  TIKTOK_AD: "TikTok ad",
  INSTAGRAM: "Instagram",
  TIKTOK: "TikTok",
  YOUTUBE_AD: "YouTube ad",
  YOUTUBE: "YouTube",
  CONTENT_PAGE: "site",
}

/** Last path segment of a URL, query stripped — the closest thing to a name a CDN file has. */
const fileNameOf = (url: string) => {
  try {
    const segment = new URL(url).pathname.split("/").filter(Boolean).pop()
    return decodeURIComponent(segment ?? "")
  } catch {
    return url.split("/").pop() ?? url
  }
}

/** An unfilled slot: pick from the product / library, or upload into it. */
function EmptySlot({
  label,
  kind,
  busy,
  onPick,
  onUpload,
}: {
  label: string
  kind: MediaKind
  busy: boolean
  onPick: () => void
  onUpload: () => void
}) {
  return (
    <div className="flex h-24 w-24 shrink-0 flex-col items-center justify-between rounded-system-md border border-dashed border-system-gray-800 bg-basic-black py-1.5 text-gray-500">
      <span className="text-[9px] font-semibold">{label}</span>
      <button
        type="button"
        onClick={onPick}
        title={`Pick ${kind === "video" ? "a video" : "an image"} for this slot`}
        className="flex flex-1 items-center justify-center hover:text-brand"
      >
        <PlusIcon className="h-5 w-5" />
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={onUpload}
        title={`Upload ${kind === "video" ? "a video" : "an image"} into this slot`}
        className="flex items-center gap-1 text-[9px] hover:text-brand disabled:opacity-50"
      >
        {busy ? (
          <span className="loading loading-spinner loading-xs" />
        ) : (
          <ArrowUpTrayIcon className="h-3 w-3" />
        )}
        upload
      </button>
    </div>
  )
}

/** Move-left / move-right under a filled slot. */
function SlotArrows({
  index,
  count,
  disabled,
  onMove,
}: {
  index: number
  count: number
  disabled: boolean
  onMove: (delta: -1 | 1) => void
}) {
  if (count < 2) return null
  const arrow =
    "flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-brand disabled:opacity-30 disabled:hover:bg-black/70"
  return (
    <span className="absolute bottom-1 right-1 flex gap-0.5">
      <button
        type="button"
        disabled={disabled || index === 0}
        onClick={() => onMove(-1)}
        title="Move earlier"
        className={arrow}
      >
        <ChevronLeftIcon className="h-3 w-3" />
      </button>
      <button
        type="button"
        disabled={disabled || index === count - 1}
        onClick={() => onMove(1)}
        title="Move later"
        className={arrow}
      >
        <ChevronRightIcon className="h-3 w-3" />
      </button>
    </span>
  )
}

/** Hover caption on a picker tile: file name and source, so a grid of
 *  identical packshots can still be told apart. */
function TileCaption({ candidate }: { candidate: PickerCandidate }) {
  if (!candidate.name && !candidate.source) return null
  return (
    <span className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col rounded-b-system-md bg-black/75 px-1 py-0.5 text-left opacity-0 transition-opacity group-hover:opacity-100">
      {candidate.name && (
        <span className="truncate text-[9px] text-gray-100">
          {candidate.name}
        </span>
      )}
      {candidate.source && (
        <span className="text-[9px] text-gray-400">{candidate.source}</span>
      )}
    </span>
  )
}

/** 96×96 media preview shared by slots and picker tiles. */
function Preview({
  url,
  isVideo,
  alt,
}: {
  url: string
  isVideo?: boolean
  alt: string
}) {
  return isVideo ? (
    <video
      src={url}
      muted
      playsInline
      preload="metadata"
      className="h-full w-full object-cover"
    />
  ) : (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      className="h-full w-full object-cover"
    />
  )
}

type MediaKind = "image" | "video"

const ACCEPT: Record<MediaKind, string> = { image: "image/*", video: "video/*" }

const kindOf = (file: File): MediaKind | null => {
  if (file.type.startsWith("image/")) return "image"
  if (file.type.startsWith("video/")) return "video"
  return null
}

/** A picker tile: toggles into the multi-selection; a tick marks the picked ones. */
function PickerTile({
  candidate,
  picked,
  disabled,
  title,
  onToggle,
}: {
  candidate: PickerCandidate
  picked: boolean
  disabled: boolean
  title: string
  onToggle: () => void
}) {
  const selectable = !!candidate.assetId
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onToggle}
      title={title}
      aria-pressed={picked}
      className={`group relative w-24 shrink-0 ${selectable ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
    >
      <div
        className={`h-24 w-24 overflow-hidden rounded-system-md border bg-basic-black ${
          picked
            ? "border-brand ring-2 ring-brand/60"
            : "border-system-gray-800 group-hover:border-gray-500"
        }`}
      >
        <Preview
          url={candidate.url}
          isVideo={candidate.isVideo}
          alt={candidate.label}
        />
      </div>
      <TileCaption candidate={candidate} />
      {picked && (
        <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white">
          <CheckIcon className="h-3 w-3" />
        </span>
      )}
    </button>
  )
}

export default function FeedMediaSection({
  productRefId,
}: {
  /** Shopify product id, or a color ref `<productId>_<color>` for one colorway. */
  productRefId: string
}) {
  const { data, isLoading, isError } = useGetProductFeedMediaQuery(
    { productRefId },
    { skip: !productRefId },
  )
  const [pickerKind, setPickerKind] = useState<MediaKind | null>(null)

  // Upload straight into the slots: the presign carries productRefId, so the
  // backend tags the new asset MANUAL on creation and the composition query
  // refreshes through the AssetProductTags tag — no second click to select.
  const { uploadFiles, isUploading } = useAssetUpload()
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const [uploadKind, setUploadKind] = useState<MediaKind>("image")
  const [dragging, setDragging] = useState(false)

  const upload = async (files: File[]) => {
    const usable = files.filter(file => kindOf(file) !== null)
    if (usable.length === 0 || !productRefId) return
    const uploaded = await uploadFiles(usable, { productRefId })
    // Entries without a library id are the hook's local-blob fallback: the
    // upload itself failed, so they never reached the feed.
    const persisted = uploaded.filter(img => img.assetId).length
    if (persisted > 0) {
      toast.success(
        `Added ${persisted} file${persisted === 1 ? "" : "s"} to the feed`,
      )
      setPickerKind(null)
    }
    if (persisted < usable.length) {
      toast.error(
        `${usable.length - persisted} upload${usable.length - persisted === 1 ? "" : "s"} failed`,
      )
    }
  }
  const openUpload = (kind: MediaKind) => {
    setUploadKind(kind)
    // Set accept before the dialog opens; the ref's value is cleared on change.
    requestAnimationFrame(() => uploadInputRef.current?.click())
  }
  const handleUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : []
    e.target.value = ""
    void upload(files)
  }
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    void upload(Array.from(e.dataTransfer.files ?? []))
  }
  const [libraryLimit, setLibraryLimit] = useState(LIBRARY_PAGE_SIZE)
  // Picker scope + search. "This product" = the snapshot's categorized sets
  // plus library assets tagged to the product (AdForge saves, uploads);
  // the rest is the whole library, optionally by source, always searchable.
  const [scope, setScope] = useState<PickerScope>("product")
  const [search, setSearch] = useState("")
  const changeScope = (next: PickerScope) => {
    setScope(next)
    setLibraryLimit(LIBRARY_PAGE_SIZE)
  }
  const changeSearch = (next: string) => {
    setSearch(next.trim())
    setLibraryLimit(LIBRARY_PAGE_SIZE)
  }
  // A color ref's product is the variant's item_group_id; a product ref is itself.
  const productId = data?.variant?.itemGroupId ?? productRefId
  const { data: library, isFetching: isLibraryFetching } = useListAssetsQuery(
    {
      limit: libraryLimit,
      status: "ACTIVE",
      syncStatus: "SYNCED",
      mediaType: pickerKind === "video" ? "VIDEO" : "IMAGE",
      ...(search ? { search } : {}),
      // The whole product family: assets on the product itself AND on any
      // of its colors, so a color can reuse what another color already
      // picked without hunting through the library.
      ...(scope === "product" ? { productFamilyRefId: productId } : {}),
      ...(scope !== "product" && scope !== "all" ? { sourceType: scope } : {}),
    },
    { skip: !pickerKind },
  )
  // Where each family asset is used (product / which colors), for the tile
  // caption. One batched call per page of candidates.
  const familyAssetIds = useMemo(
    () => (scope === "product" ? (library?.assets ?? []).map(a => a.id) : []),
    [scope, library],
  )
  const { data: familyTags } = useBulkProductTagsQuery(
    { assetIds: familyAssetIds },
    { skip: familyAssetIds.length === 0 },
  )
  const usedOnLabel = (assetId: string): string | undefined => {
    const refs = (familyTags?.results[assetId] ?? [])
      .map(t => t.productRefId)
      .filter(ref => ref !== productRefId) // not the color being edited
    if (refs.length === 0) return undefined
    const names = refs.map(ref => {
      if (ref === productId) return "product"
      if (ref.startsWith(`${productId}_`)) {
        return ref.slice(productId.length + 1)
      }
      return ref
    })
    return `on ${[...new Set(names)].join(", ")}`
  }
  const [tag, { isLoading: isTagging }] = useTagAssetProductMutation()
  const [untag, { isLoading: isUntagging, originalArgs: untagArgs }] =
    useUntagAssetProductMutation()
  const [reorder, { isLoading: isReordering }] =
    useReorderProductFeedMediaMutation()
  // Multi-select in the picker: tiles toggle, one "Add N" commits them all.
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [adding, setAdding] = useState(false)
  const closePicker = () => {
    setPickerKind(null)
    setPicked(new Set())
  }

  if (isLoading) {
    return <span className="loading loading-spinner loading-sm" />
  }
  if (isError || !data) {
    return (
      <p className="text-xs text-gray-400">
        Could not load the feed media for this product.
      </p>
    )
  }

  const busyAssetId = (isUntagging && untagArgs?.assetId) || null
  const slotsBusy = isReordering || isTagging || isUntagging

  const togglePick = (assetId: string) =>
    setPicked(current => {
      const next = new Set(current)
      if (next.has(assetId)) next.delete(assetId)
      else next.add(assetId)
      return next
    })
  const addPicked = async () => {
    const ids = [...picked]
    if (ids.length === 0) return
    setAdding(true)
    // Sequential, so the tags' createdAt — the slot order of never-reordered
    // picks — follows the order the user ticked them in.
    let added = 0
    for (const assetId of ids) {
      try {
        await tag({ assetId, productRefId }).unwrap()
        added += 1
      } catch {
        // counted below
      }
    }
    setAdding(false)
    if (added < ids.length) {
      toast.error(`${ids.length - added} of ${ids.length} could not be added`)
    }
    if (added > 0) closePicker()
  }
  const deselect = async (assetId: string) => {
    try {
      await untag({ assetId, productRefId }).unwrap()
    } catch {
      toast.error("Could not remove the asset from the feed")
    }
  }
  // Arrows: swap with the neighbour and send the whole order (images then
  // videos) — the server positions exactly what it is given.
  const move = async (kind: MediaKind, index: number, delta: -1 | 1) => {
    const images = data.customImages.map(c => c.assetId)
    const videos = data.customVideos.map(v => v.assetId)
    const list = kind === "image" ? images : videos
    const target = index + delta
    if (target < 0 || target >= list.length) return
    ;[list[index], list[target]] = [list[target], list[index]]
    try {
      await reorder({ productRefId, assetIds: [...images, ...videos] }).unwrap()
    } catch {
      toast.error("Could not reorder the feed media")
    }
  }

  // Dedupe across sources by S3 object path, not the full URL — the API
  // builds custom/library URLs without the region host while snapshot URLs
  // carry it, so the strings differ for the same file.
  const pathOf = (url: string) => {
    try {
      return new URL(url).pathname
    } catch {
      return url
    }
  }
  const selectedAssetIds = new Set([
    ...data.customImages.map(c => c.assetId),
    ...data.customVideos.map(c => c.assetId),
  ])
  const selectedPaths = new Set(
    [...data.customImages, ...data.customVideos].map(c => pathOf(c.url)),
  )
  const isPicked = (url: string, assetId?: string) =>
    selectedPaths.has(pathOf(url)) ||
    (!!assetId && selectedAssetIds.has(assetId))

  // The snapshot's categorized sets belong to "this product"; a search term
  // narrows them by file name like the library.
  const matchesSearch = (text: string) =>
    !search || text.toLowerCase().includes(search.toLowerCase())
  const snapshotCandidates: PickerCandidate[] =
    pickerKind === "image" && scope === "product"
      ? data.snapshotImages
          .filter(s => !isPicked(s.url, s.assetId))
          .filter(s => matchesSearch(fileNameOf(s.url)))
          .map((s, i) => ({
            key: `snap-${i}`,
            url: s.url,
            label: s.tag,
            name: fileNameOf(s.url),
            assetId: s.assetId,
          }))
      : []
  // Group the snapshot images by their existing taxonomy tag (lifestyle,
  // detail, model_studio, static, …) — the API returns them best-first per
  // set, so insertion order keeps each group's internal ranking.
  const snapshotGroups: Array<[string, PickerCandidate[]]> = []
  for (const c of snapshotCandidates) {
    const group = snapshotGroups.find(([tag]) => tag === c.label)
    if (group) group[1].push(c)
    else snapshotGroups.push([c.label, [c]])
  }
  const libraryCandidates: PickerCandidate[] = (library?.assets ?? [])
    .filter(a => !!a.url && !isPicked(a.url as string, a.id))
    .map(a => {
      const source = a.mappings?.[0]?.sourceType
      const fileName = (a.metadata as { fileName?: unknown } | null | undefined)
        ?.fileName
      return {
        key: a.id,
        url: (a.thumbnailUrl ?? a.url) as string,
        label: a.mediaType === "VIDEO" ? "video" : "library",
        name:
          typeof fileName === "string" && fileName
            ? fileName
            : fileNameOf(a.mappings?.[0]?.sourceUrl ?? a.url ?? ""),
        // In the product scope the caption says where the asset is already
        // used (product / which colors); elsewhere, where it came from.
        source:
          (scope === "product" ? usedOnLabel(a.id) : undefined) ??
          (source ? SOURCE_LABEL[source] : undefined),
        isVideo: a.mediaType === "VIDEO" && !a.thumbnailUrl,
        assetId: a.id,
      }
    })

  const emptyImageSlots = Math.max(
    0,
    data.imageSlots - data.customImages.length,
  )
  const emptyVideoSlots = Math.max(
    0,
    data.videoSlots - data.customVideos.length,
  )

  const slotBox =
    "h-24 w-24 overflow-hidden rounded-system-md border bg-basic-black"

  return (
    <div className="flex flex-col gap-3">
      {!data.inSnapshot && (
        <p className="rounded-system-md border border-basic-blue bg-basic-black p-3 text-xs text-gray-400">
          This product is not in the feed snapshot yet — Meta cannot match a row
          for it until the next pipeline run.
        </p>
      )}

      <input
        ref={uploadInputRef}
        type="file"
        accept={ACCEPT[uploadKind]}
        multiple
        className="hidden"
        onChange={handleUploadChange}
      />

      {/* The slot grid doubles as a drop zone: files dropped here upload and
          land in the next free slots. */}
      <div
        className={`flex flex-wrap gap-2 rounded-system-md border border-dashed p-1 transition-colors ${
          dragging ? "border-brand bg-brand/10" : "border-transparent"
        }`}
        onDragOver={e => {
          e.preventDefault()
          if (!dragging) setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        {data.heroImageUrl && (
          <div
            className="relative w-24 shrink-0"
            title="The merchant hero always ships as image[0]"
          >
            <div className={`${slotBox} border-system-gray-800 opacity-80`}>
              <Preview url={data.heroImageUrl} alt="hero" />
            </div>
            <span className="absolute left-1 top-1 rounded bg-black/70 px-1 py-0.5 text-[9px] font-semibold text-gray-200">
              image[0] · hero
            </span>
          </div>
        )}

        {data.customImages.map((c, i) => (
          <div key={c.assetId} className="relative w-24 shrink-0">
            <div className={`${slotBox} border-brand ring-2 ring-brand/60`}>
              <Preview url={c.url} alt={`image[${i + 1}]`} />
            </div>
            <span className="absolute left-1 top-1 rounded bg-brand/85 px-1 py-0.5 text-[9px] font-semibold text-white">
              {i < data.imageSlots ? `image[${i + 1}]` : "extra"}
            </span>
            {c.pending && (
              <span
                className="absolute bottom-1 left-1 rounded bg-amber-600/90 px-1 py-0.5 text-[9px] font-semibold text-white"
                title="File is still syncing into the media library — the CSV serves it once the download finishes"
              >
                syncing
              </span>
            )}
            <button
              type="button"
              disabled={busyAssetId === c.assetId}
              onClick={() => void deselect(c.assetId)}
              title="Remove from feed"
              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-red-600"
            >
              {busyAssetId === c.assetId ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                <XMarkIcon className="h-3 w-3" />
              )}
            </button>
            <SlotArrows
              index={i}
              count={data.customImages.length}
              disabled={slotsBusy}
              onMove={delta => void move("image", i, delta)}
            />
          </div>
        ))}

        {Array.from({ length: emptyImageSlots }, (_, i) => (
          <EmptySlot
            key={`empty-image-${i}`}
            label={`image[${data.customImages.length + i + 1}]`}
            kind="image"
            busy={isUploading}
            onPick={() => setPickerKind("image")}
            onUpload={() => openUpload("image")}
          />
        ))}

        {data.customVideos.map((v, i) => (
          <div key={v.assetId} className="relative w-24 shrink-0">
            <div className={`${slotBox} border-brand ring-2 ring-brand/60`}>
              <Preview url={v.url} isVideo alt={`video[${i}]`} />
            </div>
            <span className="absolute left-1 top-1 rounded bg-brand/85 px-1 py-0.5 text-[9px] font-semibold text-white">
              {i < data.videoSlots ? `video[${i}]` : "extra"}
            </span>
            {v.pending && (
              <span className="absolute bottom-1 left-1 rounded bg-amber-600/90 px-1 py-0.5 text-[9px] font-semibold text-white">
                syncing
              </span>
            )}
            <button
              type="button"
              disabled={busyAssetId === v.assetId}
              onClick={() => void deselect(v.assetId)}
              title="Remove from feed"
              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-red-600"
            >
              {busyAssetId === v.assetId ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                <XMarkIcon className="h-3 w-3" />
              )}
            </button>
            <SlotArrows
              index={i}
              count={data.customVideos.length}
              disabled={slotsBusy}
              onMove={delta => void move("video", i, delta)}
            />
          </div>
        ))}

        {Array.from({ length: emptyVideoSlots }, (_, i) => (
          <EmptySlot
            key={`empty-video-${i}`}
            label={`video[${data.customVideos.length + i}]`}
            kind="video"
            busy={isUploading}
            onPick={() => setPickerKind("video")}
            onUpload={() => openUpload("video")}
          />
        ))}
      </div>

      <p className="text-xs text-gray-500">
        {data.selectionActive
          ? "The feed serves only your selection; slots fill front-to-back. Remove everything to return to automatic."
          : "No selection — the feed auto-fills the image slots from the snapshot sets. Click a slot to serve only your picks; AdForge saves land here pre-filled."}{" "}
        Drop files onto the slots to upload them straight into the feed.
      </p>

      <Dialog
        isOpen={pickerKind !== null}
        handleClose={closePicker}
        closeOnClickOutside
        width={640}
      >
        <div className="flex max-h-[80vh] flex-col rounded-system-md bg-background-dark text-left">
          <div className="flex flex-col gap-3 border-b border-basic-blue p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">
                {pickerKind === "video" ? "Pick a video" : "Pick an image"} for
                the feed
              </h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={isUploading}
                  onClick={() => pickerKind && openUpload(pickerKind)}
                  className="btn btn-xs gap-1 border-none bg-brand text-white hover:bg-brand/80 disabled:opacity-50"
                  title="Upload from your computer straight into the feed"
                >
                  {isUploading ? (
                    <span className="loading loading-spinner loading-xs" />
                  ) : (
                    <ArrowUpTrayIcon className="h-3 w-3" />
                  )}
                  Upload
                </button>
                <button
                  type="button"
                  onClick={closePicker}
                  className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-white/10 hover:text-white"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            <DebouncedInput
              type="search"
              placeholder="Search by file name, product URL or asset id…"
              delay={300}
              defaultValue={search}
              onChange={e => changeSearch(e.target.value)}
              className="w-full"
            />
            <div className="flex flex-wrap gap-1">
              {SCOPES.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => changeScope(option.value)}
                  className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                    scope === option.value
                      ? "border-brand bg-brand/20 text-white"
                      : "border-basic-blue text-gray-400 hover:border-gray-500 hover:text-white"
                  }`}
                >
                  {option.label}
                </button>
              ))}
              {library && (
                <span className="ml-auto self-center text-[11px] text-gray-500">
                  {library.total} in library
                  {search ? ` for “${search}”` : ""}
                </span>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {snapshotGroups.map(([groupTag, items]) => (
              <div key={groupTag} className="mb-3">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {groupTag.replace(/_/g, " ")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {items.map(c => (
                    <PickerTile
                      key={c.key}
                      candidate={c}
                      picked={!!c.assetId && picked.has(c.assetId)}
                      disabled={!c.assetId || adding}
                      title={
                        c.assetId
                          ? "Select for the feed"
                          : "Not in the asset library — cannot be selected"
                      }
                      onToggle={() => c.assetId && togglePick(c.assetId)}
                    />
                  ))}
                </div>
              </div>
            ))}

            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              {scope === "product"
                ? "Saved on this product or one of its colors"
                : (SCOPES.find(s => s.value === scope)?.label ?? "Library")}
            </p>
            {isLibraryFetching && libraryCandidates.length === 0 && (
              <span className="loading loading-spinner loading-sm" />
            )}
            {!isLibraryFetching && libraryCandidates.length === 0 && (
              <p className="text-xs text-gray-500">
                {scope === "product"
                  ? `Nothing saved on this product or its colors yet${search ? ` matching “${search}”` : ""} — try “All”, or upload.`
                  : `No ${pickerKind === "video" ? "videos" : "images"} here${search ? ` matching “${search}”` : ""}.`}
              </p>
            )}
            {libraryCandidates.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {libraryCandidates.map(c => (
                  <PickerTile
                    key={c.key}
                    candidate={c}
                    picked={!!c.assetId && picked.has(c.assetId)}
                    disabled={adding}
                    title={`Select for the feed${c.name ? ` — ${c.name}` : ""}`}
                    onToggle={() => c.assetId && togglePick(c.assetId)}
                  />
                ))}
              </div>
            )}
            {library && library.total > libraryLimit && (
              <button
                type="button"
                className="btn btn-ghost btn-xs mt-2"
                disabled={isLibraryFetching}
                onClick={() => setLibraryLimit(l => l + LIBRARY_PAGE_SIZE)}
              >
                {isLibraryFetching
                  ? "Loading…"
                  : `Show more (${library.total - libraryLimit} left)`}
              </button>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-basic-blue p-3">
            <span className="text-xs text-gray-400">
              {picked.size === 0
                ? "Tick the tiles you want, then add them all at once."
                : `${picked.size} selected`}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={closePicker}
                className="btn btn-xs btn-ghost text-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={picked.size === 0 || adding}
                onClick={() => void addPicked()}
                className="btn btn-xs gap-1 border-none bg-brand text-white hover:bg-brand/80 disabled:opacity-50"
              >
                {adding && (
                  <span className="loading loading-spinner loading-xs" />
                )}
                Add {picked.size > 0 ? picked.size : ""} to the feed
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  )
}
